/**
 * Unified formats endpoints (multi-location). ONE badged list per location:
 * the shipped standard + governmental catalog templates merged with that
 * location's own uploaded configs.
 *
 *   GET /api/formats?location_id=&q=&source=&type=&family=&limit=&offset=  → FormatList
 *   GET /api/formats/facets?location_id=                            → FormatFacets
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api } from "./client";
import type { FormatFacets, FormatList, FormatQuery, FormatsOverview } from "./types";

function toParams(q: FormatQuery): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (q.location_id) params.location_id = q.location_id;
  if (q.q) params.q = q.q;
  if (q.source) params.source = q.source;
  if (q.type) params.type = q.type;
  if (q.family) params.family = q.family;
  params.limit = q.limit ?? 24;
  params.offset = q.offset ?? 0;
  return params;
}

export function useFormats(query: FormatQuery, enabled = true) {
  return useQuery({
    queryKey: ["formats", query],
    queryFn: async (): Promise<FormatList> => {
      const { data } = await api.get<FormatList>("/formats", {
        params: toParams(query),
      });
      return data;
    },
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useFormatsFacets(locationId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["formats-facets", locationId ?? null],
    queryFn: async (): Promise<FormatFacets> => {
      const { data } = await api.get<FormatFacets>("/formats/facets", {
        params: locationId ? { location_id: locationId } : {},
      });
      return data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Categorized counts for the import summary + formats page (custom / standard library
 * / site-custom). The backend is being enriched in parallel, so this degrades
 * gracefully — the consuming UI must tolerate a missing/partial response.
 */
export function useFormatsOverview(locationId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["formats-overview", locationId ?? null],
    queryFn: async (): Promise<FormatsOverview> => {
      const { data } = await api.get<FormatsOverview>("/formats/overview", {
        params: locationId ? { location_id: locationId } : {},
      });
      return data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    // The endpoint is new; never let a transient miss block the summary.
    retry: false,
  });
}
